import React from 'react';
import { Share2, Facebook, Twitter, Send, MessageCircle } from 'lucide-react';

interface ShareButtonsProps {
  title: string;
  text: string;
  shareUrl: string;
}

export const ShareButtons: React.FC<ShareButtonsProps> = ({ title, text, shareUrl }) => {
  // Safely encode text for sharing
  const safeEncode = (str: string): string => {
    try {
      // Truncate long text to prevent URI length issues
      const truncatedStr = str.length > 200 ? str.slice(0, 197) + '...' : str;
      return encodeURIComponent(truncatedStr);
    } catch (error) {
      console.error('Error encoding share text:', error);
      // Fallback to a simple message if encoding fails
      return encodeURIComponent('Check out this football analysis!');
    }
  };

  const encodedText = safeEncode(text);
  const encodedTitle = safeEncode(title);
  const encodedUrl = safeEncode(shareUrl);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: text.length > 200 ? text.slice(0, 197) + '...' : text,
          url: shareUrl,
        });
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
        <Share2 className="w-4 h-4" />
        Share:
      </span>
      
      {/* Native Share Button (Mobile) */}
      {navigator.share && (
        <button
          onClick={handleShare}
          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          aria-label="Share"
        >
          <Share2 className="w-4 h-4" />
        </button>
      )}

      {/* Facebook */}
      <a
        href={shareLinks.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
        aria-label="Share on Facebook"
      >
        <Facebook className="w-4 h-4" />
      </a>

      {/* Twitter */}
      <a
        href={shareLinks.twitter}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 text-sky-500 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-lg transition-colors"
        aria-label="Share on Twitter"
      >
        <Twitter className="w-4 h-4" />
      </a>

      {/* Telegram */}
      <a
        href={shareLinks.telegram}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 text-blue-500 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
        aria-label="Share on Telegram"
      >
        <Send className="w-4 h-4" />
      </a>

      {/* WhatsApp */}
      <a
        href={shareLinks.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-lg transition-colors"
        aria-label="Share on WhatsApp"
      >
        <MessageCircle className="w-4 h-4" />
      </a>
    </div>
  );
};